import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, MessageCircle, Heart, MapPin } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  "Retirement",
  "Child's Education",
  "Home Purchase",
  "Vacation / Sabbatical",
  "Wealth Creation",
  "Tax Saving",
  "Just exploring",
];

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("We've received your details — we'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", goal: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-muted/40 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="md:col-span-5"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium inline-flex items-center gap-2">
            <Heart className="w-3.5 h-3.5" /> We Listen
          </span>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-primary mt-3 leading-[1.1]">
            Tell us your <em className="not-italic text-secondary">pain points.</em>
          </h2>
          <p className="mt-5 text-foreground/75 leading-relaxed">
            Whether you're 22 and just got your first salary, or 52 and worried about retirement —
            our first job is to listen. No jargon, no pressure, no products forced on you.
          </p>

          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-primary/10 grid place-items-center text-primary"><Mail className="w-4 h-4" /></span>
              <a href="mailto:riddhishah2142@gmail.com" className="hover:text-primary">riddhishah2142@gmail.com</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-primary/10 grid place-items-center text-primary"><Phone className="w-4 h-4" /></span>
              <a href="tel:+919820702899" className="hover:text-primary">+91 98207 02899</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#25D366]/10 grid place-items-center text-[#1f9b4d]"><MessageCircle className="w-4 h-4" /></span>
              <a
                href="https://wa.me/919820702899?text=Hi%20Aarkays%20Team"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary"
              >
                Chat on WhatsApp
              </a>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-full bg-primary/10 grid place-items-center text-primary shrink-0"><MapPin className="w-4 h-4" /></span>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Zee+Shaan+Zee+Corporate+Park+MG+Road+Vile+Parle+East+Mumbai+400057"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary leading-relaxed"
              >
                D-508, 5th Floor, Zee Shaan,<br />
                Zee Corporate Park, M.G. Road,<br />
                Vile Parle (East), Mumbai 400 057
              </a>
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="md:col-span-7"
        >
          {done ? (
            <div
              data-testid={CONTACT.success}
              className="bg-card rounded-3xl border border-border p-10 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-secondary/15 grid place-items-center mx-auto text-secondary">
                <Heart className="w-7 h-7" />
              </div>
              <h3 className="font-serif text-3xl text-primary mt-5">Thank you — we're on it.</h3>
              <p className="text-foreground/70 mt-3 max-w-md mx-auto">
                One of our advisors will personally reach out within 24 hours. Meanwhile, feel free to ping us on WhatsApp.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-6 text-sm text-secondary underline underline-offset-4"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid={CONTACT.form}
              className="bg-card rounded-3xl border border-border p-8 md:p-10 space-y-6 shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Name</label>
                  <input
                    data-testid={CONTACT.name}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="w-full mt-1.5 bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
                  <input
                    data-testid={CONTACT.email}
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="w-full mt-1.5 bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</label>
                  <input
                    data-testid={CONTACT.phone}
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 …"
                    className="w-full mt-1.5 bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Primary Goal</label>
                  <select
                    data-testid={CONTACT.goal}
                    value={form.goal}
                    onChange={update("goal")}
                    className="w-full mt-1.5 bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none transition-colors"
                  >
                    <option value="">Choose…</option>
                    {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Tell us what's on your mind</label>
                <textarea
                  data-testid={CONTACT.message}
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="What is your biggest financial worry right now?"
                  className="w-full mt-1.5 bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none transition-colors resize-none"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
                <p className="text-xs text-muted-foreground max-w-xs">
                  By submitting, you agree to be contacted by Aarkays Consultancy regarding your enquiry.
                </p>
                <button
                  type="submit"
                  data-testid={CONTACT.submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:translate-y-0"
                >
                  {submitting ? "Sending…" : (<>Talk to an Advisor <Send className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
